import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Container,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Layout from '../../components/layout/Layout';
import hsnReportService from '../../services/hsnReportService';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatCurrency';
import * as XLSX from 'xlsx';

const HSNReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    rows: [],
    itemRows: [],
    summary: null
  });

  // Default to current month
  const getCurrentMonthDates = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date();
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const [startDate, setStartDate] = useState(getCurrentMonthDates().startDate);
  const [endDate, setEndDate] = useState(getCurrentMonthDates().endDate);
  const [hsnCodeSearch, setHsnCodeSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  const formatIgstAmountCell = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return '-';
    return formatCurrency(n);
  };

  // Fetch report data
  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await hsnReportService.getHSNReport(startDate, endDate);
      setReportData(response);
    } catch (error) {
      console.error('Error fetching HSN report:', error);
      toast.error('Failed to load HSN report');
    } finally {
      setLoading(false);
    }
  };

  // Load report on mount and when dates change
  useEffect(() => {
    fetchReportData();
  }, [startDate, endDate]);

  const formatExcelDateRange = (start, end) => {
    const fmt = (iso) => {
      if (!iso) return '';
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    };
    return `From ${fmt(start)} to ${fmt(end)}`;
  };

  // Export to Excel — two sheets: HSN summary (same as table) + item-wise breakdown
  const handleExport = async () => {
    try {
      setIsExporting(true);

      const exportData = reportData.rows.map(row => ({
        'HSN Code': row.hsnCode,
        'GST %': `${row.gstRate}%`,
        'Taxable Value (₹)': row.taxableValue || 0,
        'Total Sold Products': row.totalSoldProducts || 0,
        'IGST Amount (₹)': Number(row.igst) > 0 ? Number(row.igst) : '-',
        'CGST Amount (₹)': row.cgst || 0,
        'SGST Amount (₹)': row.sgst || 0,
        'Total GST Amount (₹)': row.totalGSTAmount || 0,
        'Total Value Sold (₹)': row.totalValueSold || 0,
      }));

      if (reportData.summary) {
        exportData.push({});
        exportData.push({
          'HSN Code': 'TOTAL',
          'GST %': '',
          'Taxable Value (₹)': reportData.summary.totalTaxableValue || 0,
          'Total Sold Products': reportData.summary.totalSoldProducts || 0,
          'IGST Amount (₹)': Number(reportData.summary.totalIGST) > 0
            ? Number(reportData.summary.totalIGST)
            : '-',
          'CGST Amount (₹)': reportData.summary.totalCGST || 0,
          'SGST Amount (₹)': reportData.summary.totalSGST || 0,
          'Total GST Amount (₹)': reportData.summary.totalGSTAmount || 0,
          'Total Value Sold (₹)': reportData.summary.totalValueSold || 0,
        });
      }

      const wsHsn = XLSX.utils.aoa_to_sheet([
        [formatExcelDateRange(startDate, endDate)],
        ['HSN Wise Summary'],
        [],
      ]);
      if (exportData.length > 0) {
        XLSX.utils.sheet_add_json(wsHsn, exportData, { origin: 'A4' });
      } else {
        XLSX.utils.sheet_add_aoa(wsHsn, [['No data']], { origin: 'A4' });
      }

      const rawItemRows = reportData.itemRows || [];
      const itemExport = rawItemRows.map(row => ({
        'HSN Code': row.hsnCode,
        'Item Name': row.productName,
        'Total Qty': row.quantityLabel || '',
        'GST %': `${row.gstRate}%`,
        'Taxable Value (₹)': row.taxableValue || 0,
        'IGST Amount (₹)': Number(row.igst) > 0 ? Number(row.igst) : '-',
        'CGST Amount (₹)': row.cgst || 0,
        'SGST Amount (₹)': row.sgst || 0,
        'Total GST Amount (₹)': row.totalGSTAmount || 0,
        'Total Value Sold (₹)': row.totalValueSold || 0,
      }));

      if (reportData.summary) {
        itemExport.push({});
        itemExport.push({
          'HSN Code': 'TOTAL',
          'Item Name': '',
          'Total Qty': `Count ${rawItemRows.length}`,
          'GST %': '',
          'Taxable Value (₹)': reportData.summary.totalTaxableValue || 0,
          'IGST Amount (₹)': Number(reportData.summary.totalIGST) > 0
            ? Number(reportData.summary.totalIGST)
            : '-',
          'CGST Amount (₹)': reportData.summary.totalCGST || 0,
          'SGST Amount (₹)': reportData.summary.totalSGST || 0,
          'Total GST Amount (₹)': reportData.summary.totalGSTAmount || 0,
          'Total Value Sold (₹)': reportData.summary.totalValueSold || 0,
        });
      }

      const wsItems = XLSX.utils.aoa_to_sheet([
        [formatExcelDateRange(startDate, endDate)],
        ['Item Details'],
        [],
      ]);
      if (itemExport.length > 0) {
        XLSX.utils.sheet_add_json(wsItems, itemExport, { origin: 'A4' });
      } else {
        XLSX.utils.sheet_add_aoa(wsItems, [['No product-level lines for this period']], { origin: 'A4' });
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsHsn, 'HSN Summary');
      XLSX.utils.book_append_sheet(wb, wsItems, 'Item Details');

      const filename = `HSN_Report_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter rows by HSN code search
  const filteredRows = reportData.rows.filter(row => {
    if (!hsnCodeSearch.trim()) return true;
    return row.hsnCode && row.hsnCode.toLowerCase().includes(hsnCodeSearch.toLowerCase().trim());
  });

  // Paginated rows
  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [hsnCodeSearch]);

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/reports" color="inherit">
            Reports
          </Link>
          <Typography color="text.primary">HSN Wise Reports</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              HSN Wise Reports
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={isExporting || loading || reportData.rows.length === 0}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by HSN Code"
                  value={hsnCodeSearch}
                  onChange={(e) => setHsnCodeSearch(e.target.value)}
                  placeholder="Enter HSN code to search"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Button
                  variant="contained"
                  onClick={fetchReportData}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Report Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredRows.length === 0 ? (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                {reportData.rows.length === 0
                  ? 'No data available for the selected date range'
                  : 'No HSN codes found matching your search'}
              </Typography>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>HSN Code</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>GST %</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Taxable Value (₹)</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total Sold Products</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>IGST Amount (₹)</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>CGST Amount (₹)</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>SGST Amount (₹)</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total GST Amount (₹)</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total Value Sold (₹)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: 'action.hover' }
                          }}
                        >
                          <TableCell>{row.hsnCode}</TableCell>
                          <TableCell align="right">{row.gstRate}%</TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.taxableValue)}
                          </TableCell>
                          <TableCell align="right">{row.totalSoldProducts}</TableCell>
                          <TableCell align="right">
                            {formatIgstAmountCell(row.igst)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.cgst || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.sgst || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.totalGSTAmount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.totalValueSold)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {reportData.summary && (
                        <TableRow sx={{ bgcolor: 'primary.main', fontWeight: 'bold' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>TOTAL</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalTaxableValue)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{reportData.summary.totalSoldProducts}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatIgstAmountCell(reportData.summary.totalIGST)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalCGST || 0)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalSGST || 0)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalGSTAmount)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'white' }}>
                            <strong>{formatCurrency(reportData.summary.totalValueSold)}</strong>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredRows.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default HSNReport;

